/**
 * =============================================================================
 * SearchDialog.tsx — Dialog modale di ricerca contenuti.
 * =============================================================================
 *
 * Cerca nelle pagine pubblicate (titolo + corpo) e nei titoli di sezioni,
 * moduli e corsi visibili all'utente. L'ambito iniziale (sito / corso /
 * modulo / sezione) viene impostato dal pulsante che apre il dialog, ma
 * l'utente può cambiarlo tramite il selettore in cima.
 *
 * Funzioni avanzate (allineate al "Trova e sostituisci" dell'editor):
 *  - Opzioni: maiuscole/minuscole, parola intera, regex (filtri client-side
 *    sopra i risultati restituiti dalla RPC).
 *  - Scorciatoie: Enter / Shift+Enter scorrono i risultati, Invio apre quello
 *    evidenziato; Esc chiude.
 *  - Cronologia: ultime ricerche salvate in localStorage e suggerite quando
 *    il campo è vuoto.
 *
 * Le righe dei risultati sono cliccabili: ognuna porta alla pagina
 * corrispondente passando `?q=<termine>` (e le opzioni) nell'URL, così la
 * pagina di destinazione può evidenziare i match e attivare la barra di
 * navigazione "Occorrenza X di Y".
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ArrowRight, History, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useSearch, type SearchScope } from "./SearchProvider";
import { useContentSearch, type SearchHit } from "@/hooks/useContentSearch";

/** Etichette italiane per gli ambiti di ricerca. */
const SCOPE_LABELS: Record<SearchScope, string> = {
  site: "Tutto il sito",
  course: "Corso corrente",
  module: "Modulo corrente",
  section: "Sezione corrente",
};

/** Chiave usata per persistere le ricerche recenti. */
const RECENT_KEY = "lni:recent-searches";
const RECENT_MAX = 8;

/** Legge la cronologia ricerche da localStorage. */
function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

/** Aggiunge una voce in cima alla cronologia, deduplicata. */
function pushRecent(term: string): string[] {
  const t = term.trim();
  if (t.length < 2) return loadRecent();
  const list = [t, ...loadRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(
    0,
    RECENT_MAX,
  );
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage pieno o non disponibile: ignoriamo silenziosamente. */
  }
  return list;
}

/** Escapa per RegExp. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Costruisce la RegExp client-side coerente con quella di highlightTerm. */
function buildClientRegex(
  term: string,
  opts: { caseSensitive: boolean; wholeWord: boolean; regex: boolean },
): RegExp | null {
  const t = term.trim();
  if (!t) return null;
  const flags = opts.caseSensitive ? "g" : "gi";
  try {
    if (opts.regex) {
      if (opts.wholeWord) return new RegExp(`(?:^|\\b)(?:${t})(?:\\b|$)`, flags);
      return new RegExp(t, flags);
    }
    const esc = escapeRegex(t);
    if (opts.wholeWord) {
      return new RegExp(
        `(?<![\\p{L}\\p{N}_])(?:${esc})(?![\\p{L}\\p{N}_])`,
        flags + "u",
      );
    }
    return new RegExp(esc, flags);
  } catch {
    return null;
  }
}

/** Rimuove i tag HTML dal frammento (lo snippet della RPC contiene <mark>…). */
function stripHtml(html: string): string {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || "";
}

export function SearchDialog() {
  const { isOpen, close, scope, scopeId, scopeLabel, setScope } = useSearch();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // Opzioni di ricerca avanzata — replicano quelle del "Trova e sostituisci".
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [regexMode, setRegexMode] = useState(false);

  const [recent, setRecent] = useState<string[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Pulisce il campo a ogni apertura e ricarica la cronologia.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIdx(0);
      setRecent(loadRecent());
    }
  }, [isOpen]);

  const { data, isFetching, error } = useContentSearch({
    query,
    scope,
    scopeId,
    enabled: isOpen,
  });

  // Filtro client-side sopra ai risultati della RPC, in base alle opzioni.
  // La RPC fa già una ricerca full-text accent-insensitive in italiano; qui
  // restringiamo (non possiamo allargare) imponendo case/parola/regex.
  const filtered = useMemo<SearchHit[]>(() => {
    const hits = data ?? [];
    const trimmed = query.trim();
    if (trimmed.length < 2) return hits;
    // Opzioni di default → nessun filtro extra (manteniamo lo stemming italiano).
    if (!caseSensitive && !wholeWord && !regexMode) return hits;
    const re = buildClientRegex(trimmed, { caseSensitive, wholeWord, regex: regexMode });
    if (!re) return hits;
    return hits.filter((h) => {
      const haystack = `${h.page_title}\n${stripHtml(h.snippet || "")}`;
      re.lastIndex = 0;
      return re.test(haystack);
    });
  }, [data, query, caseSensitive, wholeWord, regexMode]);

  // Lista lineare per navigazione da tastiera.
  const flatHits = filtered;
  useEffect(() => {
    // Quando cambia query/risultati riporta il focus sul primo.
    setActiveIdx(0);
  }, [query, caseSensitive, wholeWord, regexMode, filtered.length]);

  // Scrolla l'elemento attivo nella vista.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-hit-index="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, flatHits.length]);

  const grouped = useMemo(() => groupHits(filtered), [filtered]);

  const effectiveScopeOptions: SearchScope[] = useMemo(() => {
    const opts: SearchScope[] = ["site"];
    if (scope === "course" || scope === "module" || scope === "section") opts.push(scope);
    return opts;
  }, [scope]);

  /** Naviga al risultato selezionato e salva il termine in cronologia. */
  const handleOpen = (hit: SearchHit) => {
    const term = query.trim();
    pushRecent(term);
    const params = new URLSearchParams();
    params.set("q", term);
    params.set("pageId", hit.page_id);
    if (caseSensitive) params.set("case", "1");
    if (wholeWord) params.set("word", "1");
    if (regexMode) params.set("regex", "1");
    const courseKey = hit.course_slug || hit.course_id;
    navigate(`/corso/${courseKey}?${params.toString()}#search-${hit.page_id}`);
    close();
  };

  /** Tastiera nel campo di input: Enter naviga, Shift+Enter indietro, Esc chiude. */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(flatHits.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (flatHits.length === 0) return;
      if (e.shiftKey) {
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else {
        const hit = flatHits[activeIdx];
        if (hit) handleOpen(hit);
      }
    }
  };

  /** Cancella tutta la cronologia. */
  const clearRecent = () => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch { /* noop */ }
    setRecent([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => (!v ? close() : null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Search className="w-4 h-4" />
            Cerca nei contenuti
          </DialogTitle>
          <DialogDescription className="text-xs">
            Premi <kbd className="px-1 border rounded">Enter</kbd> per aprire,
            <kbd className="px-1 border rounded ml-1">↑/↓</kbd> per scorrere,
            <kbd className="px-1 border rounded ml-1">Esc</kbd> per chiudere.
          </DialogDescription>
        </DialogHeader>

        {/* Selettore ambito */}
        <div className="px-4 pb-2 flex flex-wrap items-center gap-2">
          <ToggleGroup
            type="single"
            value={scope}
            onValueChange={(v) => v && setScope(v as SearchScope, scopeId, scopeLabel)}
            className="flex-wrap"
          >
            {effectiveScopeOptions.map((s) => (
              <ToggleGroupItem key={s} value={s} size="sm" className="text-xs h-7">
                {SCOPE_LABELS[s]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {scope !== "site" && scopeLabel && (
            <Badge variant="secondary" className="text-xs truncate max-w-[40ch]" title={scopeLabel}>
              {scopeLabel}
            </Badge>
          )}
        </div>

        {/* Campo di input */}
        <div className="px-4 pb-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi una parola o una frase (min. 2 caratteri)…"
            className="h-9"
            aria-label="Termine di ricerca"
          />
        </div>

        {/* Opzioni avanzate */}
        <div className="px-4 pb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
          <div className="flex items-center gap-1.5">
            <Switch
              id="search-case"
              checked={caseSensitive}
              onCheckedChange={setCaseSensitive}
              className="scale-75"
            />
            <Label htmlFor="search-case" className="cursor-pointer">
              Aa <span className="text-muted-foreground">(maiuscole/minuscole)</span>
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Switch
              id="search-word"
              checked={wholeWord}
              onCheckedChange={setWholeWord}
              className="scale-75"
            />
            <Label htmlFor="search-word" className="cursor-pointer">
              [ab] <span className="text-muted-foreground">(parola intera)</span>
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Switch
              id="search-regex"
              checked={regexMode}
              onCheckedChange={setRegexMode}
              className="scale-75"
            />
            <Label htmlFor="search-regex" className="cursor-pointer">
              .* <span className="text-muted-foreground">(regex)</span>
            </Label>
          </div>
          {query.trim().length >= 2 && !isFetching && (
            <Badge variant="outline" className="ml-auto text-xs">
              {flatHits.length === 0
                ? "Nessun risultato"
                : `${flatHits.length} risultat${flatHits.length === 1 ? "o" : "i"}`}
            </Badge>
          )}
        </div>

        {/* Area risultati / cronologia */}
        <div ref={listRef} className="flex-1 overflow-y-auto border-t">
          {isFetching && (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Ricerca in corso…
            </div>
          )}

          {error && (
            <div className="p-4 text-sm text-destructive">
              Errore durante la ricerca. Riprova fra qualche secondo.
            </div>
          )}

          {/* Cronologia: visibile solo a campo vuoto e dialog aperto. */}
          {!isFetching && query.trim().length < 2 && (
            <div className="p-4">
              {recent.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center">
                  Inserisci almeno 2 caratteri per avviare la ricerca.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <History className="w-3.5 h-3.5" /> Ricerche recenti
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={clearRecent}
                    >
                      <X className="w-3 h-3 mr-1" /> Cancella
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {recent.map((r) => (
                      <Button
                        key={r}
                        variant="secondary"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setQuery(r)}
                      >
                        {r}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {!isFetching && query.trim().length >= 2 && flatHits.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">
              Nessun risultato per “{query}”.
            </div>
          )}

          {grouped.map((courseGroup) => (
            <div key={courseGroup.courseId} className="border-b last:border-b-0">
              <div className="sticky top-0 z-10 bg-muted/70 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">
                {courseGroup.courseTitle}
              </div>
              {courseGroup.modules.map((mod) => (
                <div key={mod.moduleId} className="px-2 py-1">
                  <div className="px-2 text-[11px] font-medium text-muted-foreground uppercase">
                    {mod.moduleTitle}
                  </div>
                  {mod.sections.map((sec) => (
                    <div key={sec.sectionId} className="ml-2">
                      <div className="px-2 pt-1 text-[11px] text-muted-foreground italic">
                        {sec.sectionTitle}
                      </div>
                      <ul className="space-y-1 py-1">
                        {sec.hits.map((hit) => {
                          // Indice globale del hit nella lista lineare per
                          // sincronizzare l'evidenza con la tastiera.
                          const idx = flatHits.indexOf(hit);
                          const isActive = idx === activeIdx;
                          return (
                            <li key={hit.page_id}>
                              <Button
                                data-hit-index={idx}
                                variant={isActive ? "secondary" : "ghost"}
                                size="sm"
                                className="w-full justify-start h-auto py-2 text-left whitespace-normal"
                                onMouseEnter={() => setActiveIdx(idx)}
                                onClick={() => handleOpen(hit)}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{hit.page_title}</div>
                                  <div
                                    className="text-xs text-muted-foreground line-clamp-2 [&_mark]:bg-yellow-200 [&_mark]:text-foreground [&_mark]:px-0.5 [&_mark]:rounded"
                                    dangerouslySetInnerHTML={{ __html: hit.snippet || "" }}
                                  />
                                </div>
                                <ArrowRight className="w-3.5 h-3.5 opacity-50 shrink-0 ml-2" />
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Raggruppa i risultati per corso → modulo → sezione mantenendo l'ordine di rank. */
function groupHits(hits: SearchHit[]) {
  const courses = new Map<string, ReturnType<typeof newCourse>>();
  for (const h of hits) {
    let course = courses.get(h.course_id);
    if (!course) {
      course = newCourse(h);
      courses.set(h.course_id, course);
    }
    let mod = course.modules.find((m) => m.moduleId === h.module_id);
    if (!mod) {
      mod = { moduleId: h.module_id, moduleTitle: h.module_title, sections: [] };
      course.modules.push(mod);
    }
    let sec = mod.sections.find((s) => s.sectionId === h.section_id);
    if (!sec) {
      sec = { sectionId: h.section_id, sectionTitle: h.section_title, hits: [] };
      mod.sections.push(sec);
    }
    sec.hits.push(h);
  }
  return Array.from(courses.values());
}

function newCourse(h: SearchHit) {
  return {
    courseId: h.course_id,
    courseTitle: h.course_title,
    modules: [] as Array<{
      moduleId: string;
      moduleTitle: string;
      sections: Array<{
        sectionId: string;
        sectionTitle: string;
        hits: SearchHit[];
      }>;
    }>,
  };
}
